<template>
  <div v-if="visible" :id="uid" class="comp-modal" :class="{ visible }" @mousedown="handleContainerClick">
    <div ref="container" class="container" :class="{ 'align-top': alignTop }" tabindex="0">
      <div class="content">
        <span v-if="title" class="modal-title">{{ title }}</span>
        <slot><p>{{ text }}</p></slot>
      </div>
      <div v-if="actionButtons" class="actions">
        <Button
          v-for="action in actionButtons"
          :key="action.key"
          :icon="action.icon"
          :class="action.class"
          :icon-only="action.iconOnly"
          :disabled="action.disabled"
          @click="handleActionClick(action.value)">
          {{ action.label }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
  import Vue from 'vue';
  import Button from '@/components/ui/inputs/Button';
  import Key from '@/objects/KeyboardKeys';
  import Event from '@/objects/DomEvents';

  //----------------------------------
  // Private Constants
  //----------------------------------
  const CSS_BODY_LOCK = 'locked';

  //----------------------------------
  // Private Properties
  //----------------------------------
  let uid = 0;

  export default {
    name: 'Modal',
    components: {
      Button,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      id: {
        type: String,
        default: null,
      },
      title: {
        type: String,
        default: null,
      },
      text: {
        type: String,
        default: null,
      },
      actions: {
        type: Array,
        default: null,
      },
      formAction: {
        type: String,
        default: null,
      },
      immediate: {
        type: Boolean,
        default: false,
      },
      userClosable: {
        type: Boolean,
        default: true,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      uid += 1;
      return {
        uid: this.id || `modal-${uid}`,
        visible: false,
        alignTop: false,
        userCloseEnabled: this.userClosable,
      };
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {},
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      actionButtons() {
        const out = [];
        const actions = this.actions || [];
        const numActions = actions.length;
        actions.forEach((action, index) => {
          const btnData = {
            ...action,
          };
          if (!btnData.class) {
            btnData.class = (index + 1 < numActions) ? 'secondary' : 'primary';
          }
          out.push(btnData);
        });
        return out.length > 0 ? out : null;
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    filters: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    mounted() {
      document.body.appendChild(this.$el);
      if (this.immediate) {
        this.show();
      }
    },
    // beforeUpdate() {},
    // updated() {},
    beforeDestroy() {
      if (this.$el.parentNode) {
        this.$el.parentNode.removeChild(this.$el);
      }
    },
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      show() {
        window.addEventListener(Event.KEY_DOWN, this.handleKeyboardEvents);
        this.visible = true;
        Vue.nextTick(() => {
          const { container } = this.$refs;
          const form = this.$el.querySelector('form');
          const input = this.$el.querySelector('input');
          if (form) {
            form.addEventListener('submit', this.handleFormSubmit);
          } else {
            container.focus();
          }

          if (input) {
            input.focus();
          }

          if (container.clientHeight >= window.innerHeight) {
            this.alignTop = true;
            document.body.classList.add(CSS_BODY_LOCK);
          }
        });
      },
      update() {
        const { container } = this.$refs;
        if (container.clientHeight >= window.innerHeight) {
          this.alignTop = true;
          document.body.classList.add(CSS_BODY_LOCK);
        }
      },
      close() {
        window.removeEventListener(Event.KEY_DOWN, this.handleKeyboardEvents);
        this.visible = false;
        this.$emit('close');
        if (this.immediate) {
          this.$destroy();
        }
        document.body.classList.remove(CSS_BODY_LOCK);
      },
      disableUserClose() {
        this.userCloseEnabled = false;
      },
      enableUserClose() {
        this.userCloseEnabled = true;
      },
      //----------------------------------
      // Event Handler
      //----------------------------------
      handleActionClick(value) {
        this.$emit('action', value);
      },
      handleFormSubmit(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        if (this.formAction) {
          this.$emit('action', this.formAction);
        }
      },
      handleKeyboardEvents(evt) {
        if ([Key.ESCAPE, Key.ESCAPE_ALT].includes(evt.key) && this.userCloseEnabled) {
          this.$emit('action', 'close');
          this.close();
        }
      },
      handleContainerClick(evt) {
        if (evt.target.id === this.uid && this.userCloseEnabled) {
          this.$emit('action', 'close');
          this.close();
        }
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/variables';
@import '~scss/mixins';

.comp-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: 100;
  background-color: rgba(0, 0, 0, 0.25);
  display: inline-flex;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  overflow-x: auto;

  &.visible {
    opacity: 1;
    pointer-events: all;
    transition: opacity $default-transition;
    .container {
      opacity: 1;
      transition: opacity $default-transition 0.3s;
    }
  }

  .container {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-self: center;
    background-color: $color-white-solid-10;
    border-radius: $default-border-radius;
    min-width: 500px;
    filter: drop-shadow(0px 2px 4px rgba(16, 0, 0, 0.1));
    padding: 32px;
    opacity: 0;
    outline: 0;
    // min-height: 120vh;

    &.align-top {
      align-self: flex-start;
      margin: 32px 0;
    }

    .content {
      & > * {
      }

      span.modal-title {
        margin-top: 0;
        display: block;
        text-transform: uppercase;
        font-weight: bold;

        & + p {
        }
      }

      p {
        font-size: 1.6rem;
        line-height: 1.5;
      }
    }

    .actions {
      margin-top: 32px;
      display: flex;
      justify-content: flex-start;

      button {
        margin-right: 16px;
        &.icon.only {
          min-width: auto;
        }
        &:last-of-type {
          margin-right: 0;
        }
      }
    }
  }

  &.flex {
    .content {
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
    .actions {
      flex-shrink: 0; // Safari
    }
  }
}
</style>
